import React, { useEffect, useRef, useState } from 'react';
import { TrashIcon } from '../icons/trash.icon';
import { useTranslation } from 'react-i18next';
import { ToastService } from 'services/toast.service';
import { FileIcon } from '@umanforge/common';

const toastService: ToastService = new ToastService( 'SendFileInput' );

interface SendFileInputProps {
  label?: string;
  disable?: boolean;
  multipleFiles?: boolean;
  acceptedFormats?: string[];
  maxFileSize?: number;
  clearAfterChange?: boolean
  onChange?: ( files: File[] ) => void;
}

const SendFileInput: React.FC<SendFileInputProps> = ( {
  label = 'Déposer vos fichiers',
  disable = false,
  multipleFiles = true,
  acceptedFormats = [],
  maxFileSize,
  clearAfterChange,
  onChange
} ) => {
  const { t } = useTranslation();
  const [ files, setFiles ] = useState<File[]>( [] );
  const [ isDragging, setIsDragging ] = useState( false );
  const inputRef = useRef<HTMLInputElement>( null );

  useEffect( () => {
    if ( onChange ) {
      onChange( files );
    }
    if ( clearAfterChange && files.length > 0 ) {
      setFiles( [] );
    }
  }, [ files ] );

  const handleDrop = ( e: React.DragEvent<HTMLDivElement> ) => {
    e.preventDefault();
    setIsDragging( false );

    const droppedFiles = Array.from( e.dataTransfer.files );
    addNewFiles( droppedFiles );
  };

  const handleDragOver = ( e: React.DragEvent<HTMLDivElement> ) => {
    e.preventDefault();
  };

  const handleDragEnter = () => {
    if ( !disable ) {setIsDragging( true );}
  };

  const handleDragLeave = () => {
    setIsDragging( false );
  };

  const handleChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
    if ( e.target.files ) {
      const selectedFiles = Array.from( e.target.files );
      addNewFiles( selectedFiles );
    }
  };

  const handleRemove = ( index: number ) => {
    setFiles( prev => {return prev.filter( ( _, i ) => {return i !== index;} );} );
  };

  const addNewFiles = ( newFiles: File[] ) => {
    if ( disable ) {return;}
    const validFiles = newFiles.filter( file => {
      const isMaxFileSize = ( maxFileSize == null || ( file.size / 1000 ) >= maxFileSize );
      if ( maxFileSize && isMaxFileSize ) {
        toastService.showWarning(
          t( 'translation:file_is_larger_than_max_size' )
            .replace( '{0}', file.name )
            .replace( '{1}', String( ( maxFileSize / 1000 ).toFixed( 1 ) ) )
        );
      }
      return ( acceptedFormats.length === 0 || acceptedFormats.includes( file.type ) ) && !isMaxFileSize;
    } );
    if ( validFiles.length ) {
      setFiles( prev => {return [ ...prev, ...validFiles ];} );
    }
  };

  return (
    <>
      <label className='text-gray-600'>
        {label}
      </label>
      <div
        className={`h-auto min-h-60 rounded-xl relative border-2 border-dashed flex flex-col items-center justify-center w-full trans-fast p-4 gap-4
          ${disable ? 'bg-gray-base-veryLight border-gray-base-veryLight text-gray-400 cursor-not-allowed pointer-events-none' : 'bg-white text-black border-orange-base-light hover:bg-orange-100 hover:border-black'}
          ${isDragging && !disable ? 'bg-orange-100' : ''}
          focus:border-black focus:ring-1 focus:ring-black`
        }
        onDragOver={disable ? undefined : handleDragOver}
        onDrop={disable ? undefined : handleDrop}
        onDragEnter={disable ? undefined : handleDragEnter}
        onDragLeave={disable ? undefined : handleDragLeave}
        aria-disabled={disable}
        tabIndex={disable ? -1 : 0}
      >
        <div className="flex-center flex-col gap-4">
          <div className="font-bold text-xl font-texte">{multipleFiles ? t( 'common:drop_files' ) : t( 'common:drop_file' ) }</div>
          <div>{t( 'common:or' )}</div>
          <label
            className={`flex-center gap-2 px-4 py-2 rounded-xl shadow-md transition
              ${disable ? 'bg-gray-base-veryLight text-gray-400 cursor-not-allowed pointer-events-none' : 'bg-orange-500 text-white cursor-pointer hover:bg-orange-600'}`}
            tabIndex={disable ? -1 : 0}
          >
            <FileIcon className='h-7 w-7'/>
            {t( 'common:browse' )}...
            <input
              type="file"
              ref={inputRef}
              onChange={handleChange}
              disabled={disable}
              multiple={multipleFiles}
              accept={acceptedFormats.join( ',' )}
              className="hidden"
            />
          </label>
        </div>

        {files.length > 0 && (
          <div className="w-full mt-4 space-y-2 max-h-[260px] overflow-y-auto">
            {files.map( ( file, index ) => {return (
              <div
                key={index}
                className="flex items-center justify-between p-2 rounded-md bg-white shadow text-sm"
              >
                <span className="truncate max-w-[70%]">{file.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">{( file.size / 1024 ).toFixed( 1 )} KB</span>
                  <button
                    onClick={() => {return handleRemove( index );}}
                    className="text-red-600 trans-fast rounded-xl p-2 hover:bg-red-100"
                  >
                    <TrashIcon size={5} />
                  </button>
                </div>
              </div>
            );} )}
          </div>
        )}
      </div>
    </>
  );
};

export default SendFileInput;
